import {
  $getNodeByKey,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  ElementFormatType,
  FORMAT_ELEMENT_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  NodeKey
} from 'lexical';

import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents';

import * as React from 'react';
import { withDeleteButton } from '../withDeleteButton';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { $isYouTubeNode } from './YouTubeNode';
import { mergeRegister } from '@lexical/utils';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';

type YouTubeComponentProps = Readonly<{
  className: Readonly<{
    base: string;
    focus: string;
  }>;
  format: ElementFormatType | null;
  nodeKey: NodeKey;
  videoID: string;
  width: 'inherit' | number;
  height: 'inherit' | number;
}>;

const YouTubeContainer = React.forwardRef<
  HTMLIFrameElement,
  {
    nodeKey: string;
    style: any;
    videoID: string;
    width: 'inherit' | number;
    height: 'inherit' | number;
    isEmbed: boolean;
  }
>((props, ref) => (
  <div style={{ lineHeight: 0, width: '100%' }}>
    <iframe
      ref={ref}
      width={props.width}
      height={props.height}
      src={`https://www.youtube-nocookie.com/embed/${props.videoID}`}
      frameBorder="0"
      style={{
        ...props.style,
        display: 'block',
        verticalAlign: 'top',
        width: '100%'
      }}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen={true}
      title="YouTube video"
    />
  </div>
));

const YouTubeContainerWithDelete = withDeleteButton(YouTubeContainer);

export function YouTubeComponent({
  className,
  format: initialFormat,
  nodeKey,
  videoID,
  width: initialWidth,
  height: initialHeight
}: YouTubeComponentProps) {
  const [editor] = useLexicalComposerContext();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isEditable] = useState(() => editor.isEditable());
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);

  const defaultWidth = 560;
  const defaultHeight = 315;
  const containerWidth = typeof initialWidth === 'number' ? initialWidth : defaultWidth;
  const containerHeight = typeof initialHeight === 'number' ? initialHeight : defaultHeight;

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        (event) => {
          if (isSelected) {
            event.preventDefault();
            editor.update(() => {
              const node = $getNodeByKey(nodeKey);
              if ($isYouTubeNode(node)) {
                node.remove();
              }
            });
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        (event) => {
          if (isSelected) {
            event.preventDefault();
            editor.update(() => {
              const node = $getNodeByKey(nodeKey);
              if ($isYouTubeNode(node)) {
                node.remove();
              }
            });
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        CLICK_COMMAND,
        (payload: MouseEvent) => {
          const event = payload;
          if (event.target === iframeRef.current) {
            if (event.shiftKey) {
              setSelected(!isSelected);
            } else {
              clearSelection();
              setSelected(true);
            }
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [clearSelection, editor, isSelected, nodeKey, setSelected]);

  // Keep track of the active handlers so we can properly clean them up
  const activeHandlersRef = useRef<{
    move: ((e: PointerEvent) => void) | null;
    up: ((e: PointerEvent) => void) | null;
  }>({
    move: null,
    up: null
  });

  const resizeRef = useRef<{
    startWidth: number;
    startX: number;
    ratio: number;
    isResizing: boolean;
  }>({
    startWidth: 0,
    startX: 0,
    ratio: 16 / 9,
    isResizing: false
  });

  // Clean up any existing handlers
  const cleanupHandlers = useCallback(() => {
    if (activeHandlersRef.current.move) {
      document.removeEventListener('pointermove', activeHandlersRef.current.move);
      activeHandlersRef.current.move = null;
    }
    if (activeHandlersRef.current.up) {
      document.removeEventListener('pointerup', activeHandlersRef.current.up);
      document.removeEventListener('pointercancel', activeHandlersRef.current.up);
      activeHandlersRef.current.up = null;
    }
    document.body.style.cursor = '';
    resizeRef.current.isResizing = false;
    setIsResizing(false);
  }, []);

  useEffect(() => {
    return () => {
      cleanupHandlers();
    };
  }, [cleanupHandlers]);

  const handleResizeStart = (event: React.PointerEvent<HTMLDivElement>, isLeft: boolean) => {
    if (!isEditable || !containerRef.current) return;

    cleanupHandlers();

    event.preventDefault();
    event.stopPropagation();

    const iframe = iframeRef.current;
    if (!iframe) return;

    const blocker = document.createElement('div');
    blocker.style.position = 'fixed';
    blocker.style.inset = '0';
    blocker.style.cursor = 'ew-resize';
    blocker.style.userSelect = 'none';
    blocker.style.zIndex = '9999';
    document.body.appendChild(blocker);

    const { width } = iframe.getBoundingClientRect();

    resizeRef.current = {
      startWidth: width,
      startX: event.clientX,
      ratio: 16 / 9,
      isResizing: true
    };
    setIsResizing(true);

    const handleMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      moveEvent.stopPropagation();

      if (!resizeRef.current.isResizing || !iframe || !containerRef.current) return;

      const diff = moveEvent.clientX - resizeRef.current.startX;
      const newWidth = isLeft
        ? resizeRef.current.startWidth - diff
        : resizeRef.current.startWidth + diff;

      const minWidth = 320;
      const editorWidth = editor.getRootElement()?.offsetWidth ?? 1000;
      const maxWidth = editorWidth - 40;
      const clampedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
      const newHeight = Math.round(clampedWidth / resizeRef.current.ratio);

      requestAnimationFrame(() => {
        if (iframe && containerRef.current) {
          iframe.style.width = `${clampedWidth}px`;
          iframe.style.height = `${newHeight}px`;
          containerRef.current.style.width = `${clampedWidth}px`;
        }
      });
    };

    const handleUp = (upEvent: PointerEvent) => {
      upEvent.preventDefault();
      upEvent.stopPropagation();

      if (!iframe) return;

      const finalWidth = parseInt(iframe.style.width, 10);
      const finalHeight = parseInt(iframe.style.height, 10);

      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if ($isYouTubeNode(node)) {
          node.setWidthAndHeight(finalWidth, finalHeight);
        }
      });

      document.body.removeChild(blocker);
      cleanupHandlers();
    };

    activeHandlersRef.current.move = handleMove;
    activeHandlersRef.current.up = handleUp;

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
    document.addEventListener('pointercancel', handleUp);
  };

  return (
    <BlockWithAlignableContents className={className} format="center" nodeKey={nodeKey}>
      <div
        ref={containerRef}
        className="group relative w-full"
        style={{ width: containerWidth }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {isEditable && (
          <div className="absolute inset-0" style={{ pointerEvents: 'none' }}>
            {/* Left resize handle */}
            {
              <div
                className="absolute left-0 top-1/2 -translate-x-full -translate-y-1/2 px-2"
                style={{ pointerEvents: 'auto' }}
              >
                <div
                  className={`w-1.5 h-16 rounded-full bg-[#22c55e] hover:bg-[#16a34a] transition-colors shadow-lg flex items-center justify-center cursor-ew-resize ${
                    isHovering ? 'opacity-100' : 'opacity-0'
                  }`}
                  onPointerDown={(e) => handleResizeStart(e, true)}
                  onPointerUp={() => cleanupHandlers()}
                  onMouseUp={() => cleanupHandlers()}
                >
                  <div className="w-0.5 h-6 bg-white/50 rounded-full" />
                </div>
              </div>
            }

            {/* Right resize handle */}
            {
              <div
                className="absolute right-0 top-1/2 translate-x-full -translate-y-1/2 px-2"
                style={{ pointerEvents: 'auto' }}
              >
                <div
                  className={`w-1.5 h-16 rounded-full bg-[#22c55e] hover:bg-[#16a34a] transition-colors shadow-lg flex items-center justify-center cursor-ew-resize ${
                    isHovering ? 'opacity-100' : 'opacity-0'
                  }`}
                  onPointerDown={(e) => handleResizeStart(e, false)}
                  onPointerUp={() => cleanupHandlers()}
                  onMouseUp={() => cleanupHandlers()}
                >
                  <div className="w-0.5 h-6 bg-white/50 rounded-full" />
                </div>
              </div>
            }
          </div>
        )}

        {/* YouTube iframe container */}
        <div
          className={`relative ${isHovering && !isResizing && isEditable ? 'ring-2 ring-[#22c55e]' : ''}`}
          style={{ lineHeight: 0, width: '100%' }}
        >
          <YouTubeContainerWithDelete
            isEmbed
            videoID={videoID}
            style={{
              display: 'block',
              width: '100%',
              height: containerHeight
            }}
            nodeKey={nodeKey}
            ref={iframeRef}
            width={initialWidth}
            height={containerHeight}
          />
        </div>
      </div>
    </BlockWithAlignableContents>
  );
}
